import { z } from 'zod';

import { isoDatetimeToDate, isoDateToDate } from './dates';
import { fixture } from './fixtures';
import { Condition, RuleGroup, ruleGroupFixture } from './groups';
import { User, userFixture } from './users';

const NominationPeriodStatus = z.enum(['open', 'upcoming', 'closed']);

export const NomineeType = z.enum(['individual', 'group']);
export type NomineeType = z.infer<typeof NomineeType>;

export const NomineeGroupType = z.enum(['department', 'location', 'team']);
export type NomineeGroupType = z.infer<typeof NomineeGroupType>;

export enum VotableNominees {
	Top5 = 'top_5',
	Top10 = 'top_10',
	All = 'all',
}

export const NominatedGroup = z.object({
	groupType: NomineeGroupType,
	key: z.string(),
	displayName: z.string(),
	userIds: z.array(z.string()).default([]),
});

export type NominatedGroup = z.infer<typeof NominatedGroup>;

export const NominationQuestionSchema = z.object({
	id: z.string(),
	questionText: z.string(),
	questionType: z.enum(['text', 'radio', 'checkbox', 'dropdown']),
	options: z.array(z.string()),
	isRequired: z.boolean(),
});

export type NominationQuestion = z.infer<typeof NominationQuestionSchema>;

export const NominationAnswerSchema = z.object({
	questionId: z.string(),
	answer: z.array(z.string()),
});

export type NominationAnswer = z.infer<typeof NominationAnswerSchema>;

// Server can send `reason: null` for question-based nominations; coerce to empty string
// so consumers can treat it uniformly without null checks. Round-trippable via z.codec.
const nullableReasonSchema = z.codec(z.string().nullable(), z.string(), {
	decode: (value) => value ?? '',
	encode: (value) => value,
});

// API may omit keys or send null for periods created before exclusions existed; normalize to [].
// Bidirectional so `encode()` round-trips for MSW/tests (Zod v4 rejects one-way transforms on encode).
const ruleExcludeConditionsCodec = z.codec(z.array(Condition).nullish(), z.array(Condition), {
	decode: (value) => value ?? [],
	encode: (value) => value,
});

export const Nomination = z.object({
	id: z.string(),
	reason: nullableReasonSchema,
	nominationPeriodId: z.string(),
	nominatorId: z.string(),
	nominatedUserId: z.string().nullable(),
	nominator: User,
	nominatedUser: User.nullable(),
	nominatedGroup: NominatedGroup.nullable().optional().default(null),
	createdAt: isoDatetimeToDate,
	answers: z.array(NominationAnswerSchema).optional().default([]),
});

export type Nomination = z.infer<typeof Nomination>;

export const AdminNominationPeriod = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string(),
	startDate: isoDateToDate,
	endDate: isoDateToDate.nullable(),
	status: NominationPeriodStatus,
	nomineeType: NomineeType.optional().default('individual'),
	nomineeGroupType: NomineeGroupType.nullable().optional().default(null),
	votingEnabled: z.boolean().optional().default(false),
	votingStartDate: isoDateToDate.nullable().optional().default(null),
	votingEndDate: isoDateToDate.nullable().optional().default(null),
});

export type AdminNominationPeriod = z.infer<typeof AdminNominationPeriod>;

export const AdminNominationPeriodIndex = AdminNominationPeriod.extend({
	// `uniqueNominatedUserCount` only counts user nominees and stays at 0 for group-mode
	// periods. `uniqueNomineeCount` keys on the nominee identity (user OR group), so the
	// admin index renders a meaningful number for both nominee shapes.
	uniqueNominatedUserCount: z.number().int().nonnegative(),
	uniqueNomineeCount: z.number().int().nonnegative(),
	totalNominations: z.number().int().nonnegative(),
	creator: User.nullable(),
});

export type AdminNominationPeriodIndex = z.infer<typeof AdminNominationPeriodIndex>;

export const AdminNominationPeriodDetail = AdminNominationPeriod.extend({
	nominationLimit: z.number().nullable(),
	nominations: z.array(Nomination),
	nomineeRulesGroup: RuleGroup.nullable(),
	nominatorRulesGroup: RuleGroup.nullable(),
	nomineeRuleExcludeConditions: ruleExcludeConditionsCodec,
	nominatorRuleExcludeConditions: ruleExcludeConditionsCodec,
	voterRulesGroup: RuleGroup.nullable().optional().default(null),
	voterRuleExcludeConditions: ruleExcludeConditionsCodec,
	votableNominees: z.enum(VotableNominees).nullable().optional().default(VotableNominees.Top5),
	favoritedNomineeKeys: z.array(z.string()),
	visibility: z.enum(['admin', 'public']),
	questions: z.array(NominationQuestionSchema).optional().default([]),
});

export type AdminNominationPeriodDetail = z.infer<typeof AdminNominationPeriodDetail>;

const nominationLimitSchema = z.codec(z.number().nullable(), z.number().or(z.literal(Infinity)).nullable(), {
	decode: (value) => {
		return value === null ? Infinity : value;
	},
	encode: (value) => {
		return value === Infinity ? null : value;
	},
});

export const UserNominationPeriod = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string(),
	nominationLimit: nominationLimitSchema,
	startDate: isoDateToDate,
	endDate: isoDateToDate.nullable(),
	status: NominationPeriodStatus,
	currentUserNominations: z.array(Nomination),
	nomineeRulesGroup: RuleGroup.nullable(),
	nomineeRuleExcludeConditions: ruleExcludeConditionsCodec,
	nomineeType: NomineeType.optional().default('individual'),
	nomineeGroupType: NomineeGroupType.nullable().optional().default(null),
	questions: z.array(NominationQuestionSchema).optional().default([]),
});

export type UserNominationPeriod = z.infer<typeof UserNominationPeriod>;

export type NominationPeriodStatus = z.infer<typeof NominationPeriodStatus>;

// Utility functions to replicate getter behavior from original classes
export const getNominationPeriodDetailPath = (id: string): string => `/admin/programs/nominations/${id}`;

export const getNominationPeriodEditPath = (id: string): string => `/admin/programs/nominations/${id}/edit`;

export const isNominationPeriodUpcoming = (period: { status: NominationPeriodStatus }): boolean => period.status === 'upcoming';

export const isNominationPeriodClosed = (period: { status: NominationPeriodStatus }): boolean => period.status === 'closed';

export const isNominationPeriodOpen = (period: { status: NominationPeriodStatus }): boolean => period.status === 'open';

export const isNominationPeriodPublic = (period: { visibility: 'admin' | 'public' }): boolean => period.visibility === 'public';

export const canSubmitNomination = (period: UserNominationPeriod): boolean => {
	const limit = period.nominationLimit === null ? Infinity : period.nominationLimit;
	return limit === Infinity || period.currentUserNominations.length < limit;
};

// Canonical nominee-uniqueness-key format mirroring NominatedGroup#uniqueness_key on
// the backend. Centralized so callers that need to dedupe groups against an existing
// set of nominee keys (e.g. the modal's already-nominated filter) don't drift from the
// shape `nominationNomineeKey` produces.
export const groupNomineeKey = (groupType: NomineeGroupType, key: string): string => `group:${groupType}:${key}`;

export const nominationNomineeKey = (nomination: Nomination): string => {
	if (nomination.nominatedGroup) {
		return groupNomineeKey(nomination.nominatedGroup.groupType, nomination.nominatedGroup.key);
	}
	return `user:${nomination.nominatedUserId ?? ''}`;
};

// Fixture functions using manual generators
export const nominationFixture = (overrides: Partial<Nomination> = {}): Nomination => ({
	id: fixture.id(),
	reason: fixture.string('Outstanding work on'),
	nominationPeriodId: fixture.id(),
	nominatorId: fixture.id(),
	nominatedUserId: fixture.id(),
	nominator: userFixture(),
	nominatedUser: userFixture(),
	nominatedGroup: null,
	createdAt: fixture.date(),
	answers: [],
	...overrides,
});

export const nominationAnswerFixture = (overrides: Partial<NominationAnswer> = {}): NominationAnswer => ({
	questionId: fixture.id(),
	answer: ['Sample answer'],
	...overrides,
});

export const nominationQuestionFixture = (overrides: Partial<NominationQuestion> = {}): NominationQuestion => ({
	id: fixture.id(),
	questionText: 'Why are you nominating them?',
	questionType: 'text',
	options: [],
	isRequired: true,
	...overrides,
});

export const adminNominationPeriodFixture = (overrides: Partial<AdminNominationPeriod> = {}): AdminNominationPeriod => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	startDate: fixture.pastDate(),
	endDate: fixture.futureDate(),
	status: 'open',
	nomineeType: 'individual',
	nomineeGroupType: null,
	votingEnabled: false,
	votingStartDate: null,
	votingEndDate: null,
	...overrides,
});

export const adminNominationPeriodIndexFixture = (overrides: Partial<AdminNominationPeriodIndex> = {}): AdminNominationPeriodIndex => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	startDate: fixture.pastDate(),
	endDate: fixture.futureDate(),
	uniqueNominatedUserCount: fixture.int(1, 20),
	uniqueNomineeCount: fixture.int(1, 20),
	totalNominations: fixture.int(1, 50),
	status: 'open',
	creator: userFixture(),
	nomineeType: 'individual',
	nomineeGroupType: null,
	votingEnabled: false,
	votingStartDate: null,
	votingEndDate: null,
	...overrides,
});

export const adminNominationPeriodDetailFixture = (overrides: Partial<AdminNominationPeriodDetail> = {}): AdminNominationPeriodDetail => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	startDate: fixture.pastDate(),
	endDate: fixture.futureDate(),
	status: 'open',
	nominationLimit: null,
	nominations: [nominationFixture(), nominationFixture()],
	nomineeRulesGroup: ruleGroupFixture(),
	nominatorRulesGroup: ruleGroupFixture(),
	nomineeRuleExcludeConditions: [],
	nominatorRuleExcludeConditions: [],
	voterRulesGroup: null,
	voterRuleExcludeConditions: [],
	votableNominees: VotableNominees.Top5,
	favoritedNomineeKeys: [],
	visibility: 'admin',
	questions: [],
	nomineeType: 'individual',
	nomineeGroupType: null,
	votingEnabled: false,
	votingStartDate: null,
	votingEndDate: null,
	...overrides,
});

export const userNominationPeriodFixture = (overrides: Partial<UserNominationPeriod> = {}): UserNominationPeriod => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	nominationLimit: 1,
	startDate: fixture.pastDate(),
	endDate: fixture.futureDate(),
	status: 'open',
	currentUserNominations: [nominationFixture()],
	nomineeRulesGroup: ruleGroupFixture(),
	nomineeRuleExcludeConditions: [],
	nomineeType: 'individual',
	nomineeGroupType: null,
	questions: [],
	...overrides,
});
